/**
 * safeToast — a crash-proof drop-in for sonner's `toast`.
 * =====================================================================
 * Backend errors raised via `sanitized_http_exception` arrive as an OBJECT
 * (`{ message, error_id }`). Passing an object straight into `toast.error(...)`
 * makes React throw "Objects are not valid as a React child" and crashes the
 * whole app into the GlobalErrorBoundary (see the 2026-06 Preview-email crash).
 *
 * This wrapper coerces any object argument into a safe display STRING via
 * `errMsg`, while passing strings, numbers, functions and JSX through untouched.
 * The rest of the sonner API (promise / custom / dismiss) is preserved verbatim.
 *
 * Usage: `import { toast } from '@/utils/safeToast';` (identical call sites).
 */
import React from 'react';
import { toast as sonnerToast } from 'sonner';
import { errMsg } from '@/utils/errMsg';

function toDisplay(content) {
  if (content == null) return content;
  const t = typeof content;
  if (t === 'string' || t === 'number' || t === 'function') return content;
  if (React.isValidElement(content)) return content;
  if (t === 'object') return errMsg(content, 'Something went wrong');
  return content;
}

function toast(content, ...rest) {
  return sonnerToast(toDisplay(content), ...rest);
}

// Content-first helpers: coerce the first arg (the message), keep options as-is.
['success', 'error', 'info', 'warning', 'message', 'loading'].forEach((name) => {
  toast[name] = (content, ...rest) => sonnerToast[name](toDisplay(content), ...rest);
});

// Pass-through helpers whose first arg isn't a plain message.
['promise', 'custom', 'dismiss'].forEach((name) => {
  toast[name] = (...args) => sonnerToast[name](...args);
});

export { toast };
export default toast;
